import React from 'react'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { Button, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'

/**
 * The single exit affordance for every merchant-facing preview of a quote — a floating pill in the
 * bottom-left corner. Both presentation versions use it so the button does not move between them:
 * v1 renders it in QuoteNextPreviewDialog, v2 in QuotePreviewDialog (v2 used to put a "Back to
 * Estimate" bar at the top of the page instead — ENG-1505).
 *
 * `position: fixed` means both callers must render it in the parent document, never inside the
 * preview iframe. iOS ignores an iframe's CSS height and grows the frame to its content, so the
 * iframe's viewport becomes the whole presentation and the pill lands at the bottom of the content
 * instead of the screen (ENG-1715).
 */
const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      position: 'fixed',
      left: theme.spacing(2),
      bottom: theme.spacing(2),
      zIndex: 20,
      textTransform: 'none',
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.paintscout.palette.brand.Blue.Blue55,
      color: theme.paintscout.palette.brand.Neutral.White,
      '&:hover': {
        boxShadow: theme.shadows[6],
        backgroundColor: theme.paintscout.palette.brand.Blue.Blue55
      },
      '@media print': {
        display: 'none'
      }
    }
  }),
  { name: 'ExitPreviewButton' }
)

export interface ExitPreviewButtonProps {
  classes?: StyleClasses<typeof useStyles>
  onClick: (ev?: React.MouseEvent) => void
}

function ExitPreviewButton(props: ExitPreviewButtonProps) {
  const classes = useStyles(props)
  const { onClick } = props

  return (
    <Button variant="contained" className={classes.root} startIcon={<ArrowBackIcon />} onClick={onClick}>
      Exit Preview
    </Button>
  )
}

export default ExitPreviewButton
